let attentionIntervalId = null;
let originalTitle = null;
let originalFaviconHref = null;
let originalFaviconType = null;
let attentionCount = 0;
let highlightedTitle = '';
let showingAttentionTitle = false;

const getDocument = () => (typeof document !== 'undefined' ? document : null);
const getWindow = () => (typeof window !== 'undefined' ? window : null);
const getNavigator = () => (typeof navigator !== 'undefined' ? navigator : null);

const getFaviconLink = () => {
  const doc = getDocument();
  if (!doc) return null;
  return doc.querySelector('link[rel~="icon"]');
};

const ensureOriginalFavicon = () => {
  const favicon = getFaviconLink();
  if (!favicon) return null;
  if (originalFaviconHref === null) {
    originalFaviconHref = favicon.getAttribute('href');
    originalFaviconType = favicon.getAttribute('type');
  }
  return favicon;
};

const restoreOriginalFavicon = () => {
  const favicon = ensureOriginalFavicon();
  if (!favicon) return;
  if (originalFaviconHref) {
    favicon.setAttribute('href', originalFaviconHref);
  }
  if (originalFaviconType) {
    favicon.setAttribute('type', originalFaviconType);
  }
};

const buildAttentionFaviconHref = (count) => {
  const badgeValue = count > 9 ? '9+' : String(Math.max(1, count));
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="18" fill="#0f766e"/>
      <path d="M19 21h26c2.8 0 5 2.2 5 5v12c0 2.8-2.2 5-5 5H30l-8 7v-7h-3c-2.8 0-5-2.2-5-5V26c0-2.8 2.2-5 5-5Z" fill="#ecfeff"/>
      <circle cx="48" cy="16" r="12" fill="#dc2626"/>
      <text x="48" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="#fff">${badgeValue}</text>
    </svg>
  `.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const applyAttentionFavicon = () => {
  const favicon = ensureOriginalFavicon();
  if (!favicon) return;
  if (showingAttentionTitle) {
    favicon.setAttribute('href', buildAttentionFaviconHref(attentionCount));
    favicon.setAttribute('type', 'image/svg+xml');
    return;
  }
  restoreOriginalFavicon();
};

const updateBadge = (count) => {
  const nav = getNavigator();
  if (!nav || typeof nav.setAppBadge !== 'function') return;
  try {
    const result = nav.setAppBadge(count);
    if (result?.catch) {
      result.catch(() => {});
    }
  } catch {
    // ignore badge failures
  }
};

const clearBadge = () => {
  const nav = getNavigator();
  if (!nav || typeof nav.clearAppBadge !== 'function') return;
  try {
    const result = nav.clearAppBadge();
    if (result?.catch) {
      result.catch(() => {});
    }
  } catch {
    // ignore badge failures
  }
};

const restoreOriginalTitle = () => {
  const doc = getDocument();
  if (!doc) return;
  if (originalTitle !== null) {
    doc.title = originalTitle;
  }
};

const applyAttentionTitle = () => {
  const doc = getDocument();
  if (!doc) return;
  doc.title = showingAttentionTitle ? highlightedTitle : (originalTitle ?? doc.title);
  applyAttentionFavicon();
};

const buildHighlightedTitle = ({ count, title, kind }) => {
  const baseTitle = originalTitle || 'BlueZap';
  const normalizedTitle = typeof title === 'string' ? title.trim() : '';
  const prefix = `(${count}) `;

  if (kind === 'transfer-request') {
    return `${prefix}Solicitacao de transferencia - ${baseTitle}`;
  }
  if (kind === 'internal-message') {
    return normalizedTitle
      ? `${prefix}Mensagem interna: ${normalizedTitle} - ${baseTitle}`
      : `${prefix}Nova mensagem interna - ${baseTitle}`;
  }
  if (kind === 'new-client') {
    return normalizedTitle
      ? `${prefix}Novo atendimento: ${normalizedTitle} - ${baseTitle}`
      : `${prefix}Novo atendimento - ${baseTitle}`;
  }
  return normalizedTitle
    ? `${prefix}Nova mensagem: ${normalizedTitle} - ${baseTitle}`
    : `${prefix}Nova mensagem - ${baseTitle}`;
};

export function requestBrowserAttention({ title, kind = 'message' } = {}) {
  const doc = getDocument();
  const win = getWindow();
  if (!doc || !win) return;
  if (doc.visibilityState === 'visible') return;

  if (originalTitle === null) {
    originalTitle = doc.title;
  }
  ensureOriginalFavicon();

  attentionCount = Math.min(99, attentionCount + 1);
  highlightedTitle = buildHighlightedTitle({
    count: attentionCount,
    title,
    kind,
  });
  showingAttentionTitle = true;
  applyAttentionTitle();
  updateBadge(attentionCount);

  if (attentionIntervalId !== null) {
    return;
  }

  attentionIntervalId = win.setInterval(() => {
    showingAttentionTitle = !showingAttentionTitle;
    applyAttentionTitle();
  }, 1000);
}

export function syncBrowserAttentionCount(count) {
  const doc = getDocument();
  if (!doc) return;

  const parsedCount = Number(count);
  const normalizedCount = Number.isFinite(parsedCount)
    ? Math.max(0, Math.floor(parsedCount))
    : 0;

  if (doc.visibilityState === 'visible') {
    clearBrowserAttention();
    return;
  }

  if (normalizedCount <= 0) {
    clearBrowserAttention();
    return;
  }

  if (originalTitle === null) {
    originalTitle = doc.title;
  }
  ensureOriginalFavicon();

  attentionCount = Math.min(99, normalizedCount);
  const baseTitle = originalTitle || doc.title || 'BlueZap';
  highlightedTitle = `(${attentionCount}) ${baseTitle}`;
  showingAttentionTitle = true;
  applyAttentionTitle();
  updateBadge(attentionCount);
}

export function clearBrowserAttention() {
  const win = getWindow();
  if (attentionIntervalId !== null && win) {
    win.clearInterval(attentionIntervalId);
  }
  attentionIntervalId = null;
  attentionCount = 0;
  highlightedTitle = '';
  showingAttentionTitle = false;
  restoreOriginalTitle();
  restoreOriginalFavicon();
  originalTitle = null;
  originalFaviconHref = null;
  originalFaviconType = null;
  clearBadge();
}
